import { useEffect, useState } from "react";
import { Drawer } from "@/components/common/drawer";
import { MultiProviderAppointmentDrawerClientForm } from "./multiProviderAppointmentDrawerClientForm";
import {
  MultiProviderAppointmentDrawerContextProvider,
  UseMultiProviderAppointmentDrawerProps,
} from "./multiProviderAppointmentDrawerContext";
import { MultiProviderAppointmentDrawerForm } from "./multiProviderAppointmentDrawerForm";
import { MultiProviderAppointmentDrawerTimeSelectionForm } from "./multiProviderAppointmentDrawerTimeSelectionForm";
import { MultiProviderAppointmentDrawerCreateSuccess } from "./successScreen/multiProviderAppointmentDrawerCreateSuccess";

type Props = {
  open: boolean;
  calendarClickDate?: string;
  isEditing?: boolean;
  onClose: () => void;
};

export const DEFAULT_CREATE_APPOINTMENT_DRAWER_WIDTH = "360px";

export enum DrawerStep {
  TIME_SELECTION = "TIME_SELECTION",
  DEFAULT = "DEFAULT",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
  CREATE_CLIENT = "CREATE_CLIENT",
}

const MultiProviderAppointmentDrawerImpl = ({
  open,
  calendarClickDate,
  isEditing,
  onClose,
}: Props) => {
  const [drawerStep, setDrawerStep] = useState(DrawerStep.DEFAULT);
  const [createdAppointmentId, setCreatedAppointmentId] = useState<
    string | null
  >(null);
  // Force user to enter Time Selection step and confirm date before creating an appointment
  const [isDateConfirmed, setIsDateConfirmed] = useState<boolean>(
    isEditing || !!calendarClickDate
  );

  useEffect(() => {
    if (open) {
      setIsDateConfirmed(isEditing || !!calendarClickDate);
    }
  }, [open, isEditing, calendarClickDate]);

  const handleCloseDrawer = () => {
    setDrawerStep(DrawerStep.DEFAULT);
    setCreatedAppointmentId(null);
    setIsDateConfirmed(false);
    onClose();
  };

  const handleCreateAppointmentSuccess = (appointmentId: string) => {
    setCreatedAppointmentId(appointmentId);
    setDrawerStep(DrawerStep.SUCCESS);
  };

  return (
    <Drawer open={open} onClose={handleCloseDrawer}>
      {
        {
          [DrawerStep.DEFAULT]: (
            <MultiProviderAppointmentDrawerForm
              onSetDrawerStep={setDrawerStep}
              onCloseDrawer={handleCloseDrawer}
              onCreateAppointmentSuccess={handleCreateAppointmentSuccess}
              isDateConfirmed={isDateConfirmed}
            />
          ),
          [DrawerStep.CREATE_CLIENT]: (
            <MultiProviderAppointmentDrawerClientForm
              onSetDrawerStep={setDrawerStep}
            />
          ),
          [DrawerStep.TIME_SELECTION]: (
            <MultiProviderAppointmentDrawerTimeSelectionForm
              onSetDrawerStep={setDrawerStep}
              onConfirmDate={() => setIsDateConfirmed(true)}
            />
          ),
          [DrawerStep.SUCCESS]: (
            <MultiProviderAppointmentDrawerCreateSuccess
              createdAppointmentId={createdAppointmentId}
              onCloseDrawer={handleCloseDrawer}
            />
          ),
        }[drawerStep]
      }
    </Drawer>
  );
};

export const MultiProviderAppointmentDrawer = ({
  appointmentId,
  ...props
}: Props & UseMultiProviderAppointmentDrawerProps) => (
  <MultiProviderAppointmentDrawerContextProvider
    calendarClickDate={props.calendarClickDate}
    appointmentId={appointmentId}
    open={props.open}
    isRebooking={props.isRebooking}
    isEditing={props.isEditing}
  >
    <MultiProviderAppointmentDrawerImpl {...props} />
  </MultiProviderAppointmentDrawerContextProvider>
);
